// src/Components/Partials/TrialBanner.tsx
import React, { useState } from "react";
import { useUser } from "../../Context/UserContext";
import NapkinSubscriptionModal from "../Billing/NapkinSubscriptionModal";
import type { PlanTier, BillingFrequency } from "../../api/billing";

function TrialBanner() {
  const { trialStatus, fetchTrialStatus, billingStatus } = useUser();
  const [showModal, setShowModal] = useState(false);

  if (!trialStatus) return null;

  const plan = (trialStatus.plan || "free") as PlanTier;
  const hasSub = trialStatus.hasSubscription;
  const isFree = plan === "free" && !hasSub;
  const isTrialing = trialStatus.isTrialing;
  const isExpired = trialStatus.trialExpired;

  // Show for: trialing users, expired trials, or free users (show usage)
  const sigUsed = billingStatus?.usage?.signaturesThisMonth ?? 0;
  const sigLimit = billingStatus?.limits?.signaturesPerMonth ?? 3;
  const showFreeUsage = isFree && !isTrialing && !isExpired && sigLimit > 0;

  if (!isTrialing && !isExpired && !showFreeUsage) return null;

  // Determine urgency colors for trialing state
  const daysRemaining = trialStatus.daysRemaining ?? 999;
  const isUrgent = isTrialing && daysRemaining <= 3;
  const isWarning = isTrialing && daysRemaining > 3 && daysRemaining <= 7;

  const barStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 20px",
    marginBottom: 16,
    fontSize: 13,
    fontWeight: 600,
    width: "100%",
    boxSizing: "border-box",
    ...(isExpired
      ? {
          background: "#fffbeb",
          borderBottom: "1px solid #fde68a",
          color: "#92400e",
        }
      : isUrgent
      ? {
          background: "#fef2f2",
          borderBottom: "1px solid #fecaca",
          color: "#dc2626",
        }
      : isWarning
      ? {
          background: "#fffbeb",
          borderBottom: "1px solid #fde68a",
          color: "#d97706",
        }
      : showFreeUsage
      ? {
          background: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
          color: "#475569",
        }
      : {
          background: "#eff6ff",
          borderBottom: "1px solid #bfdbfe",
          color: "#1e40af",
        }),
  };

  const buttonColor = isExpired
    ? "#92400e"
    : isUrgent
    ? "#dc2626"
    : isWarning
    ? "#d97706"
    : showFreeUsage
    ? "#475569"
    : "#1e40af";

  const buttonStyle: React.CSSProperties = {
    background: "none",
    border: `1px solid ${buttonColor}`,
    borderRadius: 6,
    padding: "4px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: buttonColor,
    cursor: "pointer",
    marginLeft: 12,
    whiteSpace: "nowrap",
  };

  let message = "";
  if (isExpired) {
    message = "Your free trial has ended";
  } else if (isTrialing) {
    message = `${trialStatus.daysRemaining} day${trialStatus.daysRemaining === 1 ? "" : "s"} left in your free trial`;
  } else if (showFreeUsage) {
    message = `${sigUsed} of ${sigLimit} signature${sigLimit === 1 ? "" : "s"} used this month`;
  }

  return (
    <>
      <div style={barStyle}>
        <span>{message}</span>
        <button type="button" style={buttonStyle} onClick={() => setShowModal(true)}>
          {isExpired ? "Subscribe now" : showFreeUsage ? "Upgrade" : "Subscribe"}
        </button>
      </div>

      <NapkinSubscriptionModal
        isOpen={showModal}
        currentTier={plan}
        currentFrequency={(billingStatus?.planFrequency || null) as BillingFrequency | null}
        hasSubscription={hasSub}
        onClose={() => setShowModal(false)}
        onCancel={() => setShowModal(false)}
        onSuccess={async () => {
          setShowModal(false);
          await fetchTrialStatus();
        }}
        onError={() => {}}
      />
    </>
  );
}

export default TrialBanner;
